import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ArrowLeft, Loader2, ShoppingBag, Sparkles, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const Cart = () => {
  const { items, updateQuantity, remove, total, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [addr, setAddr] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
    country: "United States",
  });
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null);
  const [giftCode, setGiftCode] = useState("");
  const [giftCard, setGiftCard] = useState(null);
  const [giftChecking, setGiftChecking] = useState(false);
  const [checking, setChecking] = useState(false);
  // key -> "pickup" | "ship". Eligible items default to "ship".
  const [fulfillment, setFulfillment] = useState({});
  // Fallback eligibility map fetched from the catalog (covers items added before this feature)
  const [pickupIds, setPickupIds] = useState({});
  // Discount-exempt product ids from the live catalog (gift boxes, subscriptions, admin-flagged)
  const [exemptIds, setExemptIds] = useState({});
  // Global local-pickup switch (admin): when on, every product is pickup-eligible
  const [pickupGlobal, setPickupGlobal] = useState(false);
  // Per-product "Handling" add-in fees: { [product_id]: { first, additional } }
  const [handlingMap, setHandlingMap] = useState({});

  useEffect(() => {
    api.get("/products").then((r) => {
      const map = {};
      const emap = {};
      const hmap = {};
      (r.data || []).forEach((p) => {
        if (p.local_pickup) map[p.id] = true;
        const cat = (p.category || "").toLowerCase();
        if (p.discount_exempt || cat === "giftboxes" || cat === "subscriptions") emap[p.id] = true;
        if (p.handling_enabled) {
          hmap[p.id] = {
            first: Number(p.handling_first ?? 10),
            additional: Number(p.handling_additional ?? 5.99),
          };
        }
      });
      setPickupIds(map);
      setExemptIds(emap);
      setHandlingMap(hmap);
    }).catch(() => {});
    api.get("/site-config").then((r) => setPickupGlobal(!!r.data.local_pickup_global)).catch(() => {});
  }, []);

  // Pre-order items follow their own fulfillment rule; others use local_pickup (or the global switch).
  const preFulfill = (i) => (i.preorder ? (i.preorder_fulfillment || "ship") : null);
  const pickupAllowed = (i) => (i.preorder ? ["pickup", "both"].includes(preFulfill(i)) : (!!i.local_pickup || !!pickupIds[i.product_id] || pickupGlobal));
  const shipAllowed = (i) => (i.preorder ? ["ship", "both"].includes(preFulfill(i)) : true);
  const showToggle = (i) => pickupAllowed(i) && shipAllowed(i);
  const isPickup = (i) => {
    if (i.preorder && preFulfill(i) === "pickup") return true;
    if (i.preorder && preFulfill(i) === "ship") return false;
    return pickupAllowed(i) && fulfillment[i.key] === "pickup";
  };
  const requiresShipping = items.some((i) => !isPickup(i));
  const hasPickup = items.some((i) => isPickup(i));

  // Gift boxes & subscriptions are excluded from public discount codes (robust against stale carts).
  const isExempt = (i) => {
    const cat = (i.category || "").toLowerCase();
    return cat === "giftboxes" || cat === "subscriptions" || !!exemptIds[i.product_id];
  };
  const discountableTotal = React.useMemo(
    () => items.reduce((s, i) => (
      isExempt(i)
        ? s
        : s + (i.price + Number(i.monogram_price || 0)) * i.quantity
    ), 0),
    [items, exemptIds]
  );

  // Prefill from logged-in user
  useEffect(() => {
    if (user) {
      setAddr((a) => ({ ...a, name: a.name || user.name || "", email: user.email }));
    }
  }, [user]);

  // Auto-check first-order eligibility when a valid email is entered
  useEffect(() => {
    const email = addr.email.trim();
    if (!email || !email.includes("@") || !email.includes(".")) {
      setDiscountApplied(null);
      return;
    }
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/discount/check", { email });
        if (cancelled) return;
        if (data.eligible) {
          const amount = +(discountableTotal * (data.percent / 100)).toFixed(2);
          if (amount > 0) {
            setDiscountApplied({ code: data.code, percent: data.percent, amount, eligible: true });
          } else {
            setDiscountApplied(null);
          }
        } else {
          setDiscountApplied(null);
        }
      } catch {
        setDiscountApplied(null);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [addr.email, discountableTotal]);

  const manualApply = async () => {
    const code = discountCode.trim().toUpperCase();
    if (code !== "WELCOME10") {
      toast.error("Invalid code");
      return;
    }
    if (!addr.email || !addr.email.includes("@")) {
      toast.error("Enter your email first");
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.post("/discount/check", { email: addr.email });
      if (data.eligible) {
        const amount = +(discountableTotal * 0.10).toFixed(2);
        if (amount > 0) {
          setDiscountApplied({ code: "WELCOME10", percent: 10, amount, eligible: true });
          toast.success("Discount applied - enjoy 10% off!");
        } else {
          setDiscountApplied(null);
          toast.error("WELCOME10 doesn't apply to gift boxes");
        }
      } else {
        toast.error("This code is for first-time orders only");
      }
    } finally {
      setChecking(false);
    }
  };

  const SHIPPING_FLAT = 10.99;
  const shippingCost = requiresShipping ? SHIPPING_FLAT : 0;
  // Handling add-in: first unit one price, each additional another (per cart line)
  const handlingTotal = React.useMemo(
    () => +items.reduce((s, i) => {
      const h = handlingMap[i.product_id];
      if (!h) return s;
      return s + h.first + h.additional * (i.quantity - 1);
    }, 0).toFixed(2),
    [items, handlingMap]
  );
  const preGiftTotal = Math.max(0, total + shippingCost + handlingTotal - (discountApplied ? discountApplied.amount : 0));
  const giftApplied = giftCard ? Math.min(Number(giftCard.balance), preGiftTotal) : 0;
  const finalTotal = Math.max(0, preGiftTotal - giftApplied);

  const applyGift = async () => {
    const code = giftCode.trim().toUpperCase();
    if (!code) return;
    setGiftChecking(true);
    try {
      const { data } = await api.get(`/giftcards/validate/${code}`);
      setGiftCard({ code: data.code, balance: data.balance });
      toast.success(`Gift card applied — $${data.balance.toFixed(2)} available`);
    } catch (e) {
      setGiftCard(null);
      toast.error(e?.response?.data?.detail || "Invalid gift card code");
    } finally {
      setGiftChecking(false);
    }
  };

  // Distinct processing terms across items in the cart
  const processingTerms = React.useMemo(() => {
    const seen = new Map();
    for (const it of items) {
      const t = (it.processing_time || "").trim();
      if (!t) continue;
      const key = t.toLowerCase();
      if (!seen.has(key)) seen.set(key, { time: t, products: [it.name] });
      else if (!seen.get(key).products.includes(it.name)) seen.get(key).products.push(it.name);
    }
    return Array.from(seen.values());
  }, [items]);
  const [acknowledged, setAcknowledged] = useState(false);
  const requiresAck = processingTerms.length > 0;

  const isValid = !!(
    addr.name && addr.email && addr.phone &&
    (!requiresShipping || (addr.line1 && addr.city && addr.state && addr.zip && addr.country))
  );

  const handleCheckout = async () => {
    if (!items.length) return;
    if (!user) {
      toast.error("Please sign in to check out.");
      nav("/login?redirect=/cart");
      return;
    }
    const missing = [];
    if (!addr.name) missing.push("full name");
    if (!addr.email) missing.push("email");
    if (!addr.phone) missing.push("phone");
    if (requiresShipping) {
      if (!addr.line1) missing.push("address");
      if (!addr.city) missing.push("city");
      if (!addr.state) missing.push("state");
      if (!addr.zip) missing.push("ZIP");
      if (!addr.country) missing.push("country");
    }
    if (missing.length) {
      toast.error(`Please complete: ${missing.join(", ")}.`);
      document.querySelector('[data-testid="checkout-name"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (requiresAck && !acknowledged) {
      toast.error("Please acknowledge the processing/production times to continue.");
      document.querySelector('[data-testid="processing-terms"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          size: i.size,
          color: i.color,
          image: i.image,
          monogram: i.monogram || null,
          fulfillment: isPickup(i) ? "pickup" : "ship",
        })),
        customer_email: addr.email,
        customer_name: addr.name,
        customer_phone: addr.phone,
        shipping_address: requiresShipping ? {
          line1: addr.line1,
          line2: addr.line2 || null,
          city: addr.city,
          state: addr.state,
          postal_code: addr.zip,
          country: addr.country,
          phone: addr.phone,
        } : null,
        origin_url: window.location.origin,
        discount_code: discountApplied ? discountApplied.code : null,
        gift_card_code: giftCard ? giftCard.code : null,
        processing_terms_acknowledged: acknowledged,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setCheckoutLoading(false);
    }
  };

  return (
    <main data-testid="cart-page" className="container-x py-12">
      <Link to="/shop" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#4A4A4A] mb-6">
        <ArrowLeft size={14} /> Continue shopping
      </Link>
      <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">Your Bag</div>
      <h1 className="font-display font-light text-4xl md:text-6xl text-black tracking-tight">
        Ready to <span className="font-black">check out.</span>
      </h1>

      {items.length === 0 ? (
        <div className="mt-12 border border-[#E5E2DA] p-16 text-center">
          <ShoppingBag size={40} className="mx-auto text-[#EC008C] mb-4" />
          <div className="font-display font-semibold text-xl text-black">Your bag is empty</div>
          <p className="text-[#4A4A4A] mt-2 font-light">Let's fill it with something meaningful.</p>
          <Link to="/shop" data-testid="cart-empty-shop" className="mt-6 inline-block btn-primary">
            Shop products
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {/* First-order promo banner */}
            <div data-testid="promo-banner" className="border border-[#EC008C] bg-[#FFF0F8] p-4 flex items-center gap-3">
              <Sparkles size={18} className="text-[#EC008C] flex-shrink-0" />
              <div className="text-sm">
                <strong className="text-black">10% off your first order.</strong>
                <span className="text-[#4A4A4A] font-light"> - Enter your email at checkout and the discount is applied automatically. Or use code </span>
                <code className="font-bold text-[#EC008C]">WELCOME10</code>.
              </div>
            </div>

            {items.map((i) => (
              <div key={i.key} data-testid={`cart-item-${i.product_id}`} className="border border-[#E5E2DA] p-4 flex gap-4">
                <img src={i.image} alt={i.name} className="w-24 h-24 object-cover border border-[#E5E2DA]" />
                <div className="flex-1">
                  <div className="font-semibold text-black">{i.name}</div>
                  <div className="text-[11px] tracking-wider uppercase text-[#4A4A4A] mt-1">
                    {i.size && <>Size: {i.size} · </>}
                    {i.color && <>Color: {i.color}</>}
                  </div>
                  {i.monogram && (
                    <div data-testid={`cart-monogram-${i.product_id}`} className="mt-1 text-[11px] tracking-wider uppercase font-semibold text-[#EC008C]">
                      Monogram: {i.monogram}{Number(i.monogram_price || 0) > 0 ? ` · +$${Number(i.monogram_price).toFixed(2)}` : " · Free"}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex items-center border border-black">
                      <button onClick={() => updateQuantity(i.key, i.quantity - 1)} className="w-8 h-8 font-bold hover:bg-black hover:text-white">−</button>
                      <div className="w-8 text-center font-bold text-sm">{i.quantity}</div>
                      <button onClick={() => updateQuantity(i.key, i.quantity + 1)} className="w-8 h-8 font-bold hover:bg-black hover:text-white">+</button>
                    </div>
                    <button
                      data-testid={`cart-remove-${i.product_id}`}
                      onClick={() => remove(i.key)}
                      className="text-[#EC008C] hover:text-black"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {i.preorder && (
                    <div data-testid={`cart-preorder-${i.product_id}`} className="mt-2 inline-flex flex-col gap-0.5 bg-[#FFF0F8] border border-[#EC008C] px-2.5 py-1.5 rounded">
                      <span className="text-[10px] tracking-widest uppercase font-black text-[#EC008C]">Pre-Order</span>
                      <span className="text-[11px] text-[#333]">{i.preorder_note || "Ships when available"}{i.preorder_date ? ` · ${i.preorder_date}` : ""}</span>
                    </div>
                  )}
                  {showToggle(i) && (
                    <div data-testid={`cart-fulfillment-${i.product_id}`} className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        data-testid={`fulfillment-ship-${i.product_id}`}
                        onClick={() => setFulfillment((f) => ({ ...f, [i.key]: "ship" }))}
                        className={`text-[11px] tracking-wider uppercase font-semibold px-3 py-1.5 border ${!isPickup(i) ? "bg-black text-white border-black" : "border-[#E5E2DA] text-[#4A4A4A]"}`}
                      >
                        Ship it
                      </button>
                      <button
                        type="button"
                        data-testid={`fulfillment-pickup-${i.product_id}`}
                        onClick={() => setFulfillment((f) => ({ ...f, [i.key]: "pickup" }))}
                        className={`text-[11px] tracking-wider uppercase font-semibold px-3 py-1.5 border ${isPickup(i) ? "bg-[#EC008C] text-white border-[#EC008C]" : "border-[#E5E2DA] text-[#4A4A4A]"}`}
                      >
                        Local pickup · Free
                      </button>
                    </div>
                  )}
                  {i.preorder && preFulfill(i) === "pickup" && (
                    <div className="mt-2 text-[11px] text-[#4A4A4A] italic">Local pickup when ready · no shipping charge</div>
                  )}
                </div>
                <div className="font-bold text-lg text-black">${((i.price + Number(i.monogram_price || 0)) * i.quantity).toFixed(2)}</div>
              </div>
            ))}
            <button onClick={clear} data-testid="cart-clear" className="text-xs tracking-wider uppercase font-semibold text-[#4A4A4A] link-under">
              Clear bag
            </button>
          </div>

          <div className="border border-black p-6 h-fit lg:sticky lg:top-32">
            <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-4">Order summary</h3>

            {!user && (
              <div data-testid="cart-signin-notice" className="mb-4 border border-black bg-[#F3E30A] p-3 flex items-start gap-2">
                <Lock size={16} className="mt-0.5 flex-shrink-0" />
                <div className="text-xs">
                  <strong>Account required.</strong> <Link to="/login?redirect=/cart" className="underline">Sign in</Link> or <Link to="/register" className="underline">create an account</Link> to check out.
                </div>
              </div>
            )}

            <div className="space-y-2 mb-5">
              <input data-testid="checkout-name" placeholder="Full name" value={addr.name}
                onChange={(e) => setAddr({ ...addr, name: e.target.value })}
                className="w-full px-3 py-2 border border-black text-sm focus:outline-none focus:border-[#EC008C]" />
              <input data-testid="checkout-email" type="email" placeholder="Email" value={addr.email}
                onChange={(e) => setAddr({ ...addr, email: e.target.value })}
                className="w-full px-3 py-2 border border-black text-sm focus:outline-none focus:border-[#EC008C]" />
              <input data-testid="checkout-phone" placeholder="Phone" value={addr.phone}
                onChange={(e) => setAddr({ ...addr, phone: e.target.value })}
                className="w-full px-3 py-2 border border-black text-sm focus:outline-none focus:border-[#EC008C]" />
              <input data-testid="checkout-line1" placeholder="Address line 1" value={addr.line1}
                onChange={(e) => setAddr({ ...addr, line1: e.target.value })}
                className="w-full px-3 py-2 border border-black text-sm focus:outline-none focus:border-[#EC008C]" />
              <input data-testid="checkout-line2" placeholder="Address line 2 (optional)" value={addr.line2}
                onChange={(e) => setAddr({ ...addr, line2: e.target.value })}
                className="w-full px-3 py-2 border border-black text-sm focus:outline-none focus:border-[#EC008C]" />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="checkout-city" placeholder="City" value={addr.city}
                  onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                  className="px-3 py-2 border border-black text-sm focus:outline-none focus:border-[#EC008C]" />
                <input data-testid="checkout-state" placeholder="State" value={addr.state}
                  onChange={(e) => setAddr({ ...addr, state: e.target.value })}
                  className="px-3 py-2 border border-black text-sm focus:outline-none focus:border-[#EC008C]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="checkout-zip" placeholder="ZIP" value={addr.zip}
                  onChange={(e) => setAddr({ ...addr, zip: e.target.value })}
                  className="px-3 py-2 border border-black text-sm focus:outline-none focus:border-[#EC008C]" />
                <input data-testid="checkout-country" placeholder="Country" value={addr.country}
                  onChange={(e) => setAddr({ ...addr, country: e.target.value })}
                  className="px-3 py-2 border border-black text-sm focus:outline-none focus:border-[#EC008C]" />
              </div>

              {/* Discount code */}
              <div className="flex gap-2 pt-2">
                <input data-testid="discount-code-input" placeholder="Discount code"
                  value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  className="flex-1 px-3 py-2 border border-[#E5E2DA] text-sm focus:outline-none focus:border-black" />
                <button data-testid="apply-discount-btn" onClick={manualApply} disabled={checking}
                  className="px-4 py-2 border border-black text-[11px] tracking-[0.18em] uppercase font-semibold hover:bg-black hover:text-white transition disabled:opacity-50">
                  Apply
                </button>
              </div>

              {/* Gift card */}
              <div className="flex gap-2 pt-2">
                <input data-testid="gift-code-input" placeholder="Gift card code"
                  value={giftCode} onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                  className="flex-1 px-3 py-2 border border-[#E5E2DA] text-sm focus:outline-none focus:border-black" />
                <button data-testid="apply-gift-btn" onClick={applyGift} disabled={giftChecking}
                  className="px-4 py-2 border border-black text-[11px] tracking-[0.18em] uppercase font-semibold hover:bg-black hover:text-white transition disabled:opacity-50">
                  Apply
                </button>
              </div>
            </div>

            <div className="border-t border-[#E5E2DA] pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-[#4A4A4A]">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">${total.toFixed(2)}</span>
              </div>
              {discountApplied && (
                <div data-testid="cart-discount-line" className="flex justify-between text-[#EC008C] font-semibold">
                  <span className="flex items-center gap-1">
                    <Check size={14} /> {discountApplied.code} (−{discountApplied.percent}%)
                  </span>
                  <span>−${discountApplied.amount.toFixed(2)}</span>
                </div>
              )}
              {checking && (
                <div className="text-xs text-[#4A4A4A] italic">Checking eligibility...</div>
              )}
              <div className="flex justify-between text-[#4A4A4A]">
                <span>Shipping <span className="text-[10px] uppercase tracking-wider">· {requiresShipping ? "flat rate" : "local pickup"}</span></span>
                <span data-testid="cart-shipping">{shippingCost > 0 ? `$${shippingCost.toFixed(2)}` : "FREE"}</span>
              </div>
              {hasPickup && (
                <div className="text-[11px] text-[#4A4A4A] italic">
                  {requiresShipping
                    ? "Some items are set for free local pickup; shipping applies to the rest."
                    : "All items set for free local pickup — no shipping charge."}
                </div>
              )}
              {handlingTotal > 0 && (
                <div className="flex justify-between text-[#4A4A4A]">
                  <span>Handling</span>
                  <span data-testid="cart-handling">${handlingTotal.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-black pt-3 flex justify-between text-lg font-bold text-black">
                <span>Total</span>
                <span data-testid="cart-total">${finalTotal.toFixed(2)}</span>
              </div>
              {giftCard && (
                <div data-testid="cart-gift-line" className="flex justify-between text-[#EC008C] font-semibold text-sm">
                  <span className="flex items-center gap-1"><Check size={14} /> Gift card {giftCard.code}</span>
                  <span>−${giftApplied.toFixed(2)}</span>
                </div>
              )}
              {giftCard && finalTotal <= 0 && (
                <div className="text-[11px] text-[#4A4A4A] italic">Gift card covers your order — no card payment needed.</div>
              )}
              {giftCard && Number(giftCard.balance) > giftApplied && (
                <div className="text-[11px] text-[#4A4A4A]">Remaining gift balance after this order: ${(Number(giftCard.balance) - giftApplied).toFixed(2)}</div>
              )}
            </div>

            {requiresAck && (
              <div data-testid="processing-terms" className="border-2 border-black bg-[#FFFBEA] p-4 text-xs mb-4">
                <div className="text-[10px] tracking-[0.24em] uppercase font-black text-black mb-2">
                  Processing / Production Times
                </div>
                <ul className="space-y-1.5 text-[#333]">
                  {processingTerms.map((t, i) => (
                    <li key={i} data-testid={`processing-term-${i}`}>
                      <strong className="text-black">{t.time}</strong>
                      <span className="text-[11px] text-[#666]"> — {t.products.join(", ")}</span>
                    </li>
                  ))}
                </ul>
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="processing-ack-checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="mt-0.5 accent-[#EC008C]"
                  />
                  <span className="text-[11px] leading-snug">
                    I understand these processing/production times begin after payment and are separate from shipping transit.
                    I agree not to dispute the order for arriving within these windows.
                  </span>
                </label>
              </div>
            )}

            <button
              data-testid="checkout-btn"
              onClick={handleCheckout}
              disabled={checkoutLoading}
              className="mt-6 w-full btn-primary justify-center disabled:opacity-60"
            >
              {checkoutLoading ? <Loader2 size={16} className="animate-spin" /> :
                !user ? "Sign in to check out" :
                !isValid ? "Complete shipping info" :
                (requiresAck && !acknowledged) ? "Acknowledge processing times" :
                "Pay with Stripe"}
            </button>
            <div data-testid="general-processing-notice" className="mt-4 text-[11px] leading-snug text-[#4A4A4A]">
              <span className="font-bold text-black">Processing &amp; shipping:</span> Please allow several business days
              for your order to be processed and prepared for shipping. Custom and made-to-order items may require
              additional production time. Processing/production time is separate from shipping carrier transit time.
            </div>
            <p className="mt-3 text-[10px] tracking-wider uppercase text-[#4A4A4A] text-center">
              Secure checkout via Stripe
            </p>
          </div>
        </div>
      )}
    </main>
  );
};

export default Cart;
